import { CircleAlertIcon, Loader2Icon, TriangleAlertIcon, UserIcon } from 'lucide-react'
import { useEffect, useRef } from 'react'

import { Tooltip } from 'slab'
import { formatMoney } from 'utils'
import type { QuotingEmployee } from 'utils/web_api.schemas'

// Column definitions for the employee table
const EMPLOYEE_COLUMNS = [
  { key: 'name', header: 'Name' },
  { key: 'date_of_birth', header: 'Date of Birth' },
  { key: 'address_line1', header: 'Address Line 1' },
  { key: 'address_line2', header: 'Address Line 2' },
  { key: 'city', header: 'City' },
  { key: 'state', header: 'State' },
  { key: 'zip', header: 'Zip' },
  { key: 'dependents', header: 'Number of Dependents' },
  { key: 'yearly_pay', header: 'Yearly Pay' },
  { key: 'pay_rate', header: 'Pay Rate' },
  { key: 'pay_type', header: 'Pay Type' },
] as const

const formatEmployeeValue = (employee: QuotingEmployee, key: string): string | React.ReactNode => {
  switch (key) {
    case 'name': {
      let icon: React.ReactNode | null = null
      let tooltipContent: string | null = null
      if ((employee.smart_census_parse_info?.parsing_errors?.length || 0) > 0) {
        icon = <CircleAlertIcon className='h-4 w-4 text-red-500' />
        tooltipContent = employee.smart_census_parse_info!.parsing_errors.join('\n')
      } else if ((employee.smart_census_parse_info?.parsing_warnings?.length || 0) > 0) {
        icon = <TriangleAlertIcon className='h-4 w-4 text-yellow-500' />
        tooltipContent = employee.smart_census_parse_info!.parsing_warnings.join('\n')
      }
      return (
        <div className='flex items-center gap-2'>
          <Tooltip enabled={tooltipContent != null} content={tooltipContent} delayDuration={0}>
            {icon}
          </Tooltip>
          <span>{employee.name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || '-'}</span>
        </div>
      )
    }
    case 'dependents':
      return String(employee.dependents.length)
    case 'yearly_pay':
      return employee.yearly_pay != null ? formatMoney(employee.yearly_pay) : '-'
    case 'pay_rate':
      return employee.pay_rate != null ? formatMoney(employee.pay_rate) : '-'
    case 'pay_type':
      return employee.pay_type ?? '-'
    default: {
      const value = employee[key as keyof QuotingEmployee]
      return value != null ? String(value) : '-'
    }
  }
}

export interface EmployeeSheetProps {
  employees: QuotingEmployee[]
  inProgress: boolean
  onRowHover?: (index: number | null) => void
  highlightedRowIndices?: Set<number>
  scrollToHighlighted?: boolean
}

export default function OutputProspectEmployeeSheet ({ employees, inProgress, onRowHover, highlightedRowIndices, scrollToHighlighted }: EmployeeSheetProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map())

  useEffect(() => {
    if (scrollToHighlighted && highlightedRowIndices && highlightedRowIndices.size > 0) {
      // Scroll to the first highlighted row within this container only
      const firstHighlightedIndex = Math.min(...highlightedRowIndices)
      const row = rowRefs.current.get(firstHighlightedIndex)
      const container = containerRef.current
      if (row && container) {
        // Center the row within the container
        const scrollTop = row.offsetTop - container.clientHeight / 2 + row.clientHeight / 2
        container.scrollTo({ top: scrollTop, behavior: 'smooth' })
      }
    }
  }, [highlightedRowIndices, scrollToHighlighted])

  return (
    <div ref={containerRef} className='border border-gray-200 rounded-md bg-white overflow-x-auto max-h-[70vh] overflow-y-auto'>
      <div className='white border border-gray-200 rounded-t-md p-2 flex items-center gap-2'>
        <UserIcon className='h-4 w-4' />
        <h3 className='text-sm font-medium text-gray-600 italic underline decoration-dotted'>
          Output Results
        </h3>
      </div>
      <table>
        <thead className='sticky top-0'>
          <tr className='bg-gray-100'>
            <th className='border border-gray-200 p-0.5' />
            {EMPLOYEE_COLUMNS.map((col) => (
              <th key={col.key} className='border border-gray-200 py-1 px-2 text-center font-bold whitespace-nowrap'>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {employees.map((employee, rowIdx) => (
            <tr
              key={employee.id}
              ref={(el) => {
                if (el) {
                  rowRefs.current.set(rowIdx, el)
                } else {
                  rowRefs.current.delete(rowIdx)
                }
              }}
              onMouseEnter={() => onRowHover?.(rowIdx)}
              onMouseLeave={() => onRowHover?.(null)}
              className={`cursor-default ${highlightedRowIndices?.has(rowIdx) ? 'bg-yellow-200' : ''}`}
            >
              <td className={`text-center italic border border-gray-200 py-1 px-2 whitespace-nowrap ${highlightedRowIndices?.has(rowIdx) ? 'bg-yellow-200' : 'bg-gray-100'}`}>
                {rowIdx + 1}
              </td>
              {EMPLOYEE_COLUMNS.map((col) => (
                <td key={col.key} className='border border-gray-100 py-1 px-2 whitespace-nowrap'>
                  {formatEmployeeValue(employee, col.key)}
                </td>
              ))}
            </tr>
          ))}
          {inProgress && (
            <tr className='bg-gray-100 animate-pulse'>
              <td className='text-center italic border border-gray-200 py-1 px-2 whitespace-nowrap bg-gray-100'>
                {employees.length + 1}
              </td>
              <td colSpan={EMPLOYEE_COLUMNS.length + 1} className='text-center italic border border-gray-200 py-1 px-2 whitespace-nowrap'>
                <Loader2Icon className='h-4 w-4 animate-spin' />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
